import { FC, useCallback, useEffect, useMemo } from "react";

import { FakeProgressOverview } from "../section/progress/FakeProgressOverview";
import { ProgressOverview } from "../section/progress/ProgressOverview";

import { HomeAssessmentList } from "./HomeAssessmentList";
import { HomeOutcomesList } from "./HomeOutcomesList";
import { HomeSectionList } from "./HomeSectionList";
import { isStaff } from "./roles";

import { SECONDARY_OVERLAY_QUERY_PARAM, TAB_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { TabBar } from "mds/components/TabBar";
import { AssessPointsMarkerIcon, FlagIcon, OutcomeHashtagIcon, TextBooksIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentUserId } from "store/selectors";
import { useQueryParamsManager } from "utils/routing";
import { HomeRoleType, homeUrlForRole, withQueryParams } from "utils/urls";
import { isStudentDemoEnabled } from "utils/waffle-flags";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

const HomeContentTabKeys = ["courses", "outcomes", "assessments", "progress"] as const;

export const HomeContentView: FC<{ userRole: HomeRoleType }> = ({ userRole }) => {
  const isStudent = !isStaff(userRole);
  const currentUserId = useAppSelector(selectCurrentUserId);

  const { getQueryParam } = useQueryParamsManager();
  const overlay = getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM);
  const urlForTab = useCallback(
    (tab: (typeof HomeContentTabKeys)[number]) => {
      // This is a fix for switching tabs accidentally closing overlays
      if (overlay) {
        return withQueryParams(homeUrlForRole(userRole), { tab, overlay });
      }
      return withQueryParams(homeUrlForRole(userRole), { tab });
    },
    [overlay, userRole],
  );

  useEffect(() => {
    if (currentUserId) {
      storeApi.section_outcomes.list();
    }
  }, [currentUserId]);

  const ProgressComponent = isStudentDemoEnabled() ? FakeProgressOverview : ProgressOverview;

  const tabs = useMemo(
    () =>
      [
        {
          key: HomeContentTabKeys[0],
          to: urlForTab(HomeContentTabKeys[0]),
          label: (
            <IconText
              iconStart={<TextBooksIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("home.tabs.courses")}
              hideIconOnMd
            />
          ),
          title: t("home.tabs.courses"),
          eventName: "Home - View course catalog",
          panel: <HomeSectionList role={userRole} />,
        },
        {
          key: HomeContentTabKeys[1],
          to: urlForTab(HomeContentTabKeys[1]),
          label: (
            <IconText
              iconStart={<OutcomeHashtagIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("home.tabs.outcomes")}
              hideIconOnMd
            />
          ),
          title: t("home.tabs.outcomes"),
          eventName: "Home - View outcomes index",
          panel: <HomeOutcomesList role={userRole} />,
        },
        {
          key: HomeContentTabKeys[2],
          to: urlForTab(HomeContentTabKeys[2]),
          label: (
            <IconText
              iconStart={<AssessPointsMarkerIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("glossary.assessments")}
              hideIconOnMd
            />
          ),
          title: t("glossary.assessments"),
          eventName: "Home - View assessments",
          panel: <HomeAssessmentList role={userRole} />,
        },
        {
          key: HomeContentTabKeys[3],
          to: urlForTab(HomeContentTabKeys[3]),
          label: (
            <IconText
              iconStart={<FlagIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("progress_overview.title")}
              hideIconOnMd
            />
          ),
          title: t("progress_overview.title"),
          eventName: "Home - View progress overview",
          panel: isStudent ? (
            <ErrorBoundary>
              <ProgressComponent withOutcomeCards />
            </ErrorBoundary>
          ) : null,
        },
      ].filter((tab) => tab.panel),
    [isStudent, userRole, urlForTab, ProgressComponent],
  );

  const activeTab =
    (getQueryParam(TAB_QUERY_PARAM) as (typeof HomeContentTabKeys)[number] | null) ??
    HomeContentTabKeys[0];
  const tabIndex = useMemo(() => tabs.map((tab) => tab.key).indexOf(activeTab), [activeTab, tabs]);

  return (
    <div className="h-full overflow-y-auto bg-white md:overflow-y-hidden">
      <TabBar className="md:h-full" selectedIndex={tabIndex} tabListClassName="gap-4" tabs={tabs} />
    </div>
  );
};
